import { enableProdMode, provideZoneChangeDetection, importProvidersFrom, ErrorHandler, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import * as Sentry from "@sentry/angular";
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import localeNlExtra from '@angular/common/locales/extra/nl';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { MatInputModule } from '@angular/material/input';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MTX_SELECT_DEFAULT_OPTIONS } from '@ng-matero/extensions/select';
import { OverlayContainer } from '@angular/cdk/overlay';
import { isDevMode } from '@angular/core';
import { LuxonDateFormatPipe } from './app/modules/general/pipes/luxonDateFormat.pipe';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { LuxonDateAdapter, MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { DutchPaginatorIntl } from './app/services/dutch-paginator-intl.service';
import { Settings } from 'luxon';
import { AppRoutes } from './app/app.routing';
import { ChunkLoadErrorHandler } from './app/services/chunk-load-error-handler';
import { MenuItems } from './app/shared/menu-items/menu-items';
import { AccordionAnchorDirective, AccordionLinkDirective, AccordionDirective } from './app/shared/accordion';
import { AuthGuard } from './app/authentication/helpers/auth.guard';
import { AuthenticationService } from './app/authentication/authentication.service';
import { ApiService } from './app/modules/api.service';
import { MaintenanceService } from './app/authentication/helpers/maintenance.service';
import { SnackBarService } from './app/modules/general/services/snackbar.service';
import { PersistenceService } from './app/modules/general/services/persistence.service';
import { OpenAIService } from './app/modules/general/services/openai.service';
import { VisionService } from './app/modules/general/services/vision.service';
import { ThemeService } from './app/services/theme.service';
import { JwtInterceptor } from './app/authentication/helpers/jwt.interceptor';
import { MaintenanceInterceptor } from './app/authentication/helpers/maintenance.interceptor';
import { provideLuxonDatetimeAdapter } from './app/adapters/mtx-luxon-datetime-adapter';

if (environment.production == true) {
  enableProdMode();

  Sentry.init({
    dsn: "https://489253ce1946ddde9cc81cc53364447a@o4505962585128960.ingest.us.sentry.io/4505962599809024",
    integrations: [
    ],
    beforeSend(event, hint) {
      // Log the error to console for local debugging
      if (hint.originalException) {
        console.error('Error captured by Sentry:', hint.originalException);
      }
      return event;
    },
  });
}

registerLocaleData(localeNl, 'nl-NL', localeNlExtra);
registerLocaleData(localeNl, 'nl', localeNlExtra);

// Luxon global settings
function configureLuxonSettings() {
  Settings.defaultZone = 'Europe/Amsterdam';
  Settings.defaultLocale = 'nl';
}

function getMtxSelectDefaultOptions() {
  return {
    appendTo: '.cdk-overlay-pane'
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    importProvidersFrom(
      BrowserModule,
      FormsModule,
      ReactiveFormsModule,
      AccordionAnchorDirective,
      AccordionLinkDirective,
      AccordionDirective,
      RouterModule.forRoot(AppRoutes, { useHash: false }),
      MatInputModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000'
      }),
      LuxonDateFormatPipe,
      MatLuxonDateModule,
    ),
    { provide: DateAdapter, useClass: LuxonDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'nl-NL' },
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: MatPaginatorIntl, useClass: DutchPaginatorIntl },
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    {
      provide: MTX_SELECT_DEFAULT_OPTIONS,
      useFactory: getMtxSelectDefaultOptions,
      deps: [OverlayContainer]
    },
    MenuItems,
    AuthGuard,
    AuthenticationService,
    ApiService,
    MaintenanceService,
    SnackBarService,
    PersistenceService,
    OpenAIService,
    VisionService,
    ThemeService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => configureLuxonSettings,
      multi: true
    },
    provideAnimations(),
    ...provideLuxonDatetimeAdapter({
      parse: {
        dateInput: 'dd-MM-yyyy',
        monthInput: 'MMMM',
        yearInput: 'yyyy',
        timeInput: 'HH:mm',
        datetimeInput: 'dd-MM-yyyy HH:mm',
      },
      display: {
        dateInput: 'dd-MM-yyyy',
        monthInput: 'MMMM',
        yearInput: 'yyyy',
        timeInput: 'HH:mm',
        datetimeInput: 'dd-MM-yyyy HH:mm',
        monthYearLabel: 'MMMM yyyy',
        dateA11yLabel: 'DDD',
        monthYearA11yLabel: 'MMMM yyyy',
        popupHeaderDateLabel: 'dd MMM, ccc',
      },
    }),
  ]
}).catch((err: unknown) => console.error(err));

if ('serviceWorker' in navigator && environment.production) {
  navigator.serviceWorker.register('/ngsw-worker.js')
    .then(registration => {
      console.log('Service Worker registered with scope:', registration.scope);
    })
    .catch(error => {
      console.error('Service Worker registration failed:', error);
    });
}
